import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Divider } from "@msuite/ui/divider";
import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { moment } from "@msuite/utils/date";
import type { ProjectTimelineNoteWithFiles } from "@msuite/utils/types";
import { UserCard } from "@/components/cards";
import { RemoteFileInspector } from "@/components/files";
import { NoteHintButton, NoteText } from "@/components/notes";
import { ProjectTimelineItemNotes } from "./project-timeline-item-notes";
import { ProjectTimelineNoteItemMenu } from "./project-timeline-note-item-menu";

/** Props Interface */
interface ProjectTimelineNoteItemProps {
	item: ProjectTimelineNoteWithFiles;
}

export function ProjectTimelineNoteItem({ item }: ProjectTimelineNoteItemProps) {
	/** Hooks */
	const notesDisclosure = useDisclosure();
	const { t } = useTranslation();
	const { language } = useTranslation();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnFilesChanged() {
		await qc.invalidateQueries({ predicate: invalidate(["timeline"]) });
	}

	/** Render */
	return (
		<div
			className="v-stack"
			key={item.id}
		>
			<div className="card v-stack">
				<div className="h-stack justify-between">
					<div className="h-stack">
						<UserCard
							variant="plain"
							userId={item.createdBy}
						/>
						<p className="label">{moment(item.note.createdAt).locale(language).longFormat()}</p>
					</div>
					<div className="h-stack gap-4">
						{item.note.isImportant && (
							<div className="h-stack text-sm">
								<StatusIndicator color="emerald" />
								<p>{t("projects.note_is_included_in_diary")}</p>
							</div>
						)}
						<ProjectTimelineNoteItemMenu item={item} />
					</div>
				</div>
				<Divider variant="card" />
				<NoteText note={item.note.note} />
				<Divider variant="card" />
				<div className="h-stack items-baseline justify-between">
					<RemoteFileInspector
						itemFiles={item.note.files}
						size="xs"
						variant="inline"
						tableName="noteFiles"
						foreignKey="noteId"
						identifier={item.note.id}
						// The files hang on the note, not on the project: `noteFiles` answers to
						// `update Note`, whatever the note is written about.
						parent={{ subject: "Note", resource: item.note }}
						projectId={item.projectId}
						withUpload
						onUnlinked={handleOnFilesChanged}
						onLinked={handleOnFilesChanged}
					/>
					<NoteHintButton
						disclosure={notesDisclosure}
						notesTable="projectTimelineNoteNotes"
						identifier={item.id}
					/>
				</div>
			</div>
			<ProjectTimelineItemNotes
				notesTable="projectTimelineNoteNotes"
				identifier={item.id}
				disclosure={notesDisclosure}
				disableIncludeInDiary={!item.note.isImportant}
			/>
		</div>
	);
}
